import { Spinner } from '../../components/Spinner.js';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { apiClient } from '../../lib/api-client.js';

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string;
  relatedSpotId: string | null;
  relatedCommentId: string | null;
  relatedConversationId: string | null;
  isRead: boolean;
  createdAt: string;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'たった今';
  if (minutes < 60) return `${minutes}分前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}時間前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}日前`;
  return new Date(iso).toLocaleDateString('ja-JP');
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
      <path
        d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.73 21a2 2 0 0 1-3.46 0"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function NotificationsView() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await apiClient.get<Notification[]>('/notifications', { requireAuth: true });
      setNotifications(data ?? []);
    } catch {
      // エラー無視
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      try {
        await apiClient.patch(`/notifications/${notification.id}/read`, {}, { requireAuth: true });
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n))
        );
        window.dispatchEvent(new CustomEvent('notifications-read'));
      } catch {
        // 既読化失敗は無視
      }
    }
    if (notification.type === 'ranking_update') {
      navigate('/ranking');
    } else if (notification.type === 'dm' && notification.relatedConversationId) {
      navigate(`/messages/${notification.relatedConversationId}`);
    } else if (notification.relatedSpotId) {
      if (notification.type === 'comment_reply' && notification.relatedCommentId) {
        navigate(
          `/spots/${notification.relatedSpotId}?tab=comments&commentId=${notification.relatedCommentId}`
        );
      } else {
        navigate(`/spots/${notification.relatedSpotId}`);
      }
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await apiClient.patch('/notifications/read-all', {}, { requireAuth: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      window.dispatchEvent(new CustomEvent('notifications-read'));
    } catch {
      // エラー無視
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* ヘッダー */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
        <h1 className="text-lg font-bold text-gray-900">通知</h1>
        {unreadCount > 0 && (
          <button
            onClick={() => void handleMarkAllRead()}
            disabled={markingAll}
            className="text-xs text-primary font-medium hover:text-primary/70 disabled:opacity-50"
          >
            すべて既読にする
          </button>
        )}
      </div>

      {/* 通知一覧 */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-gray-400">
          <BellIcon />
          <p className="text-sm">通知はありません</p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {notifications.map((notification) => (
            <li key={notification.id}>
              <button
                onClick={() => void handleNotificationClick(notification)}
                className={`w-full text-left px-4 py-4 flex gap-3 items-start transition-colors hover:bg-gray-50 active:bg-gray-100 ${
                  !notification.isRead ? 'bg-primary-50' : ''
                }`}
              >
                <div
                  className={`mt-0.5 ${!notification.isRead ? 'text-primary' : 'text-gray-300'}`}
                >
                  <BellIcon />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                  <p className="text-sm text-gray-600 mt-0.5">{notification.body}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatRelativeTime(notification.createdAt)}
                  </p>
                </div>
                {!notification.isRead && (
                  <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
